import { type FormEvent } from "react";
import { PracticeProfilesPicker } from "../../../components/practice/PracticeProfilesPicker";
import { PRACTICE_FORM_ID } from "../useOnboardingFlow";
import type {
  OfferedProfile,
  PracticePickerPhase,
  PracticeProfileOffer,
} from "../../../domains/practice/practiceProfileSelection";

/**
 * The moment a practitioner feels recognised (practice-profiles ticket 08): the
 * Specialty they just declared shows them the practices it contains.
 *
 * Visited only when their Specialty has a catalogue — 93 of the ~95 have none for
 * the whole of this lot, and those users never see this slide. The answer is
 * held in the flow store and persisted with the rest of the profile on
 * finalize, unless "Later" put the question off.
 *
 * Two questions in one slide — the axis, then the practices it selects. The axis
 * is NOT skippable here, and this is the only surface where it is not: unanswered,
 * the list spans every side at once, and at signup there is no saved answer to
 * fall back on. At least one side must be named, and as many as the practitioner
 * works across may be.
 *
 * And at least one ANSWER about the practice, which is the invariant the settings
 * editor holds too (`canSubmitPracticeProfiles`): a practice checked, or — in the
 * declarative arm — the practitioner's own words. What keeps it from being a trap
 * is that search runs over the whole catalogue: any practice in the Specialty is
 * reachable from here, including the ones the axis answer left off screen.
 */
export function PracticeStep({
  offer,
  selected,
  checkedOnOpen,
  disambiguation,
  specialtyLabel,
  onChangeSpecialty,
  phase,
  declaration,
  matching,
  isAssisting,
  onToggle,
  onToggleDisambiguation,
  onSetPractices,
  onPhaseChange,
  onSubmit,
  variant,
}: {
  offer: PracticeProfileOffer;
  selected: ReadonlySet<string>;
  checkedOnOpen: ReadonlySet<string>;
  disambiguation: readonly string[] | null;
  specialtyLabel: string | null;
  onChangeSpecialty: (() => void) | null;
  phase: PracticePickerPhase;
  /**
   * The free-text way out, or null in the control arm of
   * `PRACTICE_DECLARATION`. Resolved by the flow, which is where the flag is
   * read, so nothing below here knows an arm exists.
   */
  declaration: {
    readonly query: string;
    readonly onDeclare: (words: string) => void;
  } | null;
  matching: readonly OfferedProfile[] | null;
  isAssisting: boolean;
  onToggle: (uid: string) => void;
  onToggleDisambiguation: (value: string) => void;
  onSetPractices: (uids: readonly string[]) => void;
  onPhaseChange: (phase: PracticePickerPhase) => void;
  onSubmit: () => void;
  /** Which carousel this slide is in: see `PracticeProfilesPicker`. */
  variant: "mobile" | "desktop";
}): React.JSX.Element {
  return (
    <form
      id={PRACTICE_FORM_ID}
      className="flex w-full min-h-0 flex-col"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <PracticeProfilesPicker
        offer={offer}
        selected={selected}
        checkedOnOpen={checkedOnOpen}
        disambiguation={disambiguation}
        specialtyLabel={specialtyLabel}
        onChangeSpecialty={onChangeSpecialty}
        phase={phase}
        // The carousel's header band prints the axis question, at the display
        // size the design gives it. The picker printing it too is the same
        // question twice on one screen.
        // No "Check all" here, and that is the point of this screen's lot: it
        // was the shortest way past a question that refuses an empty answer, and
        // what came out the other side was a profile claiming the whole
        // catalogue. The way out for someone the agent did not recognise is the
        // field, which reads free text and, in the declarative arm, keeps it.
        surface={{ kind: "signup", declaration }}
        // The field itself is in the footer band, with the country, profession
        // and specialty ones: `OnboardingStepActions`.
        matching={matching}
        isAssisting={isAssisting}
        onToggle={onToggle}
        onToggleDisambiguation={onToggleDisambiguation}
        onSetPractices={onSetPractices}
        onPhaseChange={onPhaseChange}
        variant={variant}
      />
    </form>
  );
}
